import styled, { createGlobalStyle } from "styled-components";
import { TableScrollArea as BaseTableScrollArea } from "../PerformanceReport/styled";

/* Everything visual comes from Performance Report so both pages match:
   layout, top bar, range tabs, filters, summary cards, table, rows, pills. */
export {
  MainDiv,
  ContentWrapDiv,
  TopContainer,
  RangeTabsWrapper,
  RangeTab,
  FilterRow,
  StyledSelectWrap,
  StyledDatePickerWrap,
  FilterRecruiterChip,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  MainTable,
  THMeta,
  THGroup,
  THSub,
  RecruiterRow,
  RecruiterNameCell,
  RecruiterChip,
  GrandTotalRow,
  ColorPill,
  NumSpan,
  SpinWrap,
  EmptyWrap,
} from "../PerformanceReport/styled";

const C = {
  ink: "#1F2937",
  muted: "#6B7280",
  faint: "#9CA3AF",
  line: "#E5E7EB",
  soft: "#F5F6FB",
};

/* ---------- Month picker popup ----------
   antd renders the popup outside the page, so it is styled globally,
   scoped to the "review-month-picker" class only. This removes the empty
   space on the right and keeps the selected month a neat rounded chip. */
export const MonthPickerStyles = createGlobalStyle`
  /* Review drawer: our panel handles its own padding and scrolling,
     so antd's body padding and scroll are switched off here. */
  .review-drawer .ant-drawer-body {
    padding: 0 !important;
    overflow: hidden !important;
  }

  /* Month range input: centre the arrow between the two months */
  .review-range .ant-picker-range-separator {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 10px !important;
    line-height: 1 !important;
  }

  .review-range .ant-picker-input > input {
    text-align: center;
  }

  /* Each month grid is exactly as wide as its 3 columns. With a range
     picker antd shows two grids side by side, so the container stays auto. */
  .review-month-picker .ant-picker-panel-container {
    width: auto !important;
    min-width: 0 !important;
  }

  .review-month-picker .ant-picker-panel,
  .review-month-picker .ant-picker-month-panel {
    width: 280px !important;
    min-width: 0 !important;
  }

  .review-month-picker .ant-picker-header {
    padding: 0 8px;
  }

  .review-month-picker .ant-picker-body {
    padding: 8px 12px 12px !important;
  }

  .review-month-picker .ant-picker-content {
    width: 100% !important;
    height: auto !important;
    table-layout: fixed;
  }

  .review-month-picker .ant-picker-cell {
    padding: 6px 0 !important;
  }

  .review-month-picker .ant-picker-cell::before {
    height: 30px !important;
  }

  .review-month-picker .ant-picker-cell-inner {
    width: 64px !important;
    height: 30px !important;
    line-height: 30px !important;
    padding: 0 !important;
    border-radius: 8px !important;
    font-size: 14px;
  }

  /* Months between start and end of a range */
  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-in-range::before {
    background: #eef2ff !important;
  }

  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
    background: #4f46e5 !important;
    color: #ffffff !important;
    font-weight: 600;
  }

  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-end)::before,
  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-start)::before {
    background: #eef2ff !important;
  }

  .review-month-picker .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border-color: #4f46e5 !important;
    border-radius: 8px !important;
  }

  .review-month-picker .ant-picker-cell-disabled::before {
    background: transparent !important;
  }

  .review-month-picker .ant-picker-cell-disabled .ant-picker-cell-inner {
    color: #d1d5db !important;
  }
`;

/* Arrow between the two months in the range picker */
export const RangeArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`;

/* Numbers [switch] Percentages: plain, no box around it.
   The side that is currently shown is dark and bold, the other is grey. */
export const ToggleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 0 4px;
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid #4f46e5;
    outline-offset: 4px;
    border-radius: 6px;
  }
`;

export const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: ${(p) => (p.active ? 700 : 500)};
  color: ${(p) => (p.active ? "#111827" : "#9CA3AF")};
  white-space: nowrap;
  transition: color 0.15s ease;
`;

/* ---------- Header ---------- */

/* Lets the title block and the month tabs stack when space runs out */
export const TopWrap = styled.div`
  & > div {
    flex-wrap: wrap;
    row-gap: 12px;
  }
`;

export const PageTitle = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
`;

/* ---------- Summary cards (4 across) ---------- */

export const SummaryGrid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/* ---------- Table extras ---------- */

/* Same scroll area as Performance Report, plus sideways scrolling.
   When the window is narrow or zoomed in, the table keeps a sensible
   minimum width and scrolls left/right instead of cutting columns off. */
export const TableScrollArea = styled(BaseTableScrollArea)`
  overflow-x: auto !important;
  max-width: 100%;

  table {
    min-width: 1100px;
  }
`;

const tones = {
  good: ["#D1FAE5", "#065F46"],
  ok: ["#FEF3C7", "#92400E"],
  low: ["#FEE2E2", "#991B1B"],
};

export const AttendancePill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${(p) => tones[p.tone || "good"][0]};
  color: ${(p) => tones[p.tone || "good"][1]};
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

export const RowHint = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #4f46e5;
  opacity: 0;
  transition: opacity 0.15s ease;

  tr:hover & {
    opacity: 1;
  }
`;

/* ---------- Details drawer ---------- */

const D = {
  brand: "#4F46E5",
  brandSoft: "#EEF2FF",
  brandInk: "#4338CA",
};

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  color: ${C.ink};
`;

/* Everything below the header scrolls; the header never moves */
export const PanelBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

export const PanelTop = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  background: #ffffff;
  border-bottom: 1px solid ${C.line};
`;

export const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  span {
    margin-left: 6px;
    font-size: 13px;
    color: ${C.muted};
    font-variant-numeric: tabular-nums;
  }
`;

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${C.line};
  border-radius: 8px;
  background: #ffffff;
  color: ${C.ink};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${D.brandSoft};
    border-color: #c7d2fe;
    color: ${D.brandInk};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${D.brand};
    outline-offset: 2px;
  }
`;

export const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 18px;
`;

export const Avatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(p) => p.bg};
  color: ${(p) => p.fg};
  font-size: 16px;
  font-weight: 800;
`;

export const Name = styled.div`
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
  color: #111827;
`;

export const Sub = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${C.muted};
`;

export const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 0 24px 20px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const MetaItem = styled.div`
  .label {
    font-size: 12px;
    color: ${C.muted};
    margin-bottom: 4px;
  }
  .value {
    font-size: 14px;
    font-weight: 700;
    color: ${C.ink};
    font-variant-numeric: tabular-nums;
  }
`;

export const Section = styled.section`
  padding: 20px 24px;
  border-top: 1px solid ${C.line};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }
  span {
    font-size: 12px;
    color: ${C.muted};
  }
`;

/* One block per month when a range is selected */
export const CalMonth = styled.div`
  & + & {
    margin-top: 18px;
  }
`;

export const CalMonthLabel = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #4338ca;
`;

/* Month calendar: Mon–Fri columns, one row per week */
export const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
`;

export const CalHead = styled.div`
  padding-bottom: 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
  color: ${C.faint};
`;

export const CalDay = styled.div`
  grid-column: ${(p) => p.col};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: ${(p) => (p.absent ? "#FEF2F2" : D.brandSoft)};
  color: ${(p) => (p.absent ? "#DC2626" : D.brandInk)};
  border: 1px solid ${(p) => (p.absent ? "#FECACA" : "#E0E7FF")};
`;

export const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  font-size: 12px;
  color: ${C.muted};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  span::before {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
  .present::before {
    background: ${D.brandSoft};
    border: 1px solid #c7d2fe;
  }
  .absent::before {
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
`;

/* Pipeline tiles */
export const StageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

export const StageTile = styled.div`
  padding: 12px 14px;
  border: 1px solid ${C.line};
  border-radius: 12px;

  .label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${C.muted};
  }
  .rate {
    margin: 2px 0 8px;
    font-size: 12px;
    color: ${C.muted};
  }
`;

export const StageValue = styled.div`
  margin-top: 4px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
  color: ${(p) => p.color || C.ink};
  font-variant-numeric: tabular-nums;
`;

const deltaTones = {
  up: ["#ECFDF5", "#047857"],
  down: ["#FEF2F2", "#B91C1C"],
  flat: ["#F3F4F6", "#6B7280"],
};

export const DeltaChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => deltaTones[p.tone || "flat"][0]};
  color: ${(p) => deltaTones[p.tone || "flat"][1]};
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

/* Sent breakdown */
export const StackBar = styled.div`
  display: flex;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: ${C.line};
  margin-bottom: 8px;
`;

export const StackSeg = styled.span`
  width: ${(p) => p.pct}%;
  background: ${(p) => p.color};
`;

export const OutcomeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 56px 56px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${C.line};
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.muted ? C.faint : C.ink)};

  &:last-child {
    border-bottom: 0;
  }

  .name {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
  }
  .count {
    text-align: right;
    font-weight: 700;
  }
  .pct {
    text-align: right;
    color: ${C.muted};
  }
`;

export const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.color};
  opacity: ${(p) => (p.muted ? 0.35 : 1)};
`;